// src/pages/Chat.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendChatMessage } from '../services/chatService';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from '../firebase';
import Lottie from 'lottie-react';
import camelAnimation from '../assets/animations/camel-tutor.json';
import { FaVolumeUp, FaMicrophone, FaPaperPlane, FaHome } from 'react-icons/fa';
import emojiRegex from 'emoji-regex';
import { APP_CONFIG } from '../config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './Chat.css';
import { getChildren, calculateAge, isAdultAccount } from '../utils/childrenHelper';

const { AI_NAME, AI_EMOJI, AI_ROLE } = APP_CONFIG;


const getSystemMessage = (userProfile) => {
  const children = getChildren(userProfile);
  const firstChild = children[0];
  const age = firstChild ? calculateAge(firstChild.dateOfBirth) : null;

  if (isAdultAccount(userProfile)) {
    const ageContext = age ? `The student is ${age} years old.` : '';
    return `You are a knowledgeable and encouraging ${AI_ROLE} named ${AI_NAME} ${AI_EMOJI} helping an adult learner study Arabic.
${ageContext} Teach at an adult level — full explanations, nuanced examples, and longer responses where helpful.
You lead the lesson: don't ask what they want to learn; proactively choose useful, practical Arabic on a sensible beginner-to-intermediate progression, building on what they say.

Teaching approach:
- Explain clearly in English; introduce Arabic in Arabic script with pronunciation (transliteration) and meaning.
- You may write full Arabic sentences when useful, always with pronunciation and translation.
- Cover practical, conversational, and — where the learner is interested — classical/Quranic and cultural Arabic.
- Use markdown (tables, bold, headings) for structured content like vocabulary, conjugation, or the alphabet.
- Keep a warm, respectful, encouraging adult tone — supportive, not childish. Use emojis sparingly, if at all.

Keep responses focused and continue the lesson proactively; don't end with passive phrases like "let me know if you want more." Keep content respectful and free of sexual or harmful material, but you may discuss religious, cultural, and classical Arabic context where relevant to language learning, neutrally and respectfully.

Begin teaching as soon as the conversation starts. When greeting, introduce yourself as ${AI_NAME} and your role as ${AI_ROLE}.`;
  }

  const ageContext = age ? `The student is ${age} years old, so adjust your language complexity, examples, and teaching approach accordingly.` : '';

  return `You are a friendly ${AI_ROLE} named ${AI_NAME} ${AI_EMOJI} helping kids learn Arabic.
  ${ageContext}
  You are in charge of the conversation.
  Do not ask the student what they want to learn.
  Instead, you choose what to teach based on what a child that age would enjoy or benefit from.
  Your lessons should be short, engaging, and focused.

  CRITICAL SAFETY GUIDELINES - You must NEVER discuss or provide information about:
  - Sex, sexuality, romantic relationships, or anything related to physical intimacy
  - Gender identity topics, pronouns, or gender-related discussions beyond basic "boy/girl" vocabulary
  - Religion, religious practices, God, Allah, prayer, or any spiritual/religious topics
  - Violence, fighting, weapons, or harmful behavior
  - Inappropriate language, curse words, or disrespectful behavior
  - Adult topics, politics, or controversial subjects

  If a child asks about any of these topics, politely redirect them to Arabic learning with a response like: "That's an interesting question, but let's focus on learning Arabic! Let me teach you a fun word instead..."

  SAFE TOPICS ONLY:
  - Basic Arabic vocabulary (animals, colors, numbers, food, family, school items)
  - Simple greetings and polite expressions
  - Fun, educational, age-appropriate content
  - Encouragement and positive reinforcement

  Always respond primarily in English. You may include short Arabic words, but **never write full sentences in Arabic**.
  Always show Arabic words **with their English pronunciation and meaning**.
  Always:
  1. Explain things in simple English kids can understand
  2. Introduce Arabic words in Arabic script *and* how to pronounce them
  3. Keep responses short (2-4 sentences), exciting, and friendly
  4. Use emojis, stickers, and encouragement to hold their attention
  5. Use markdown formatting (e.g., tables, bold, and headings) when appropriate, especially when teaching structured content like the alphabet or vocabulary.

  You are allowed and encouraged to include markdown tables in your replies when useful.

  Example: "Let's learn a fun word today! 🐱 The word for 'cat' in Arabic is قطة (pronounced 'qitta'). Can you say 'qitta' with me?" 🎉

  Begin teaching as soon as the conversation starts. Avoid open-ended questions. Be confident, structured, and fun.
  When greeting, always provide your ${AI_NAME} and your ${AI_ROLE} and always be enthusiastic
  Never end a response with a passive phrase like "Let me know if you want to learn more."
  Always continue the conversation and the lesson proactively.
  If a student says something, use it as a springboard to the next Arabic word or concept, even if it's off-topic.`;
};

// Check if speech synthesis is supported
const checkSpeechSupport = () => {
  if (!('speechSynthesis' in window)) {
    console.warn("Speech synthesis not supported in this browser");
    return false;
  }
  return true;
};

function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [xp, setXp] = useState(0);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [speechSupported, setSpeechSupported] = useState(false);
  const speechSynth = useRef(null);
  const avatarRef = useRef();
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  // MOBILE VIEWPORT FIX
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => {
      setTimeout(setVH, 100);
    });
    
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // KEYBOARD DETECTION FOR MOBILE
  useEffect(() => {
    if (window.innerWidth <= 768) {
      if (window.visualViewport) {
        const handleViewportChange = () => {
          const isKeyboardVisible = window.visualViewport.height < window.innerHeight * 0.75;
          document.body.classList.toggle('keyboard-visible', isKeyboardVisible);
        };
        
        window.visualViewport.addEventListener('resize', handleViewportChange);
        return () => window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        // Fallback for browsers without visualViewport
        let initialHeight = window.innerHeight;
        const handleResize = () => {
          const isKeyboardVisible = window.innerHeight < initialHeight * 0.75;
          document.body.classList.toggle('keyboard-visible', isKeyboardVisible);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }
    }
  }, []);

  // Auto scroll to bottom - with mobile optimization
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      // Use different behavior for mobile vs desktop
      const isMobile = window.innerWidth <= 768;
      messagesEndRef.current.scrollIntoView({ 
        behavior: isMobile ? "auto" : "smooth",
        block: "end"
      });
    }
  };

  // Auto-scroll when messages change (separate from viewport handling)
  useEffect(() => {
    // Delay scroll on mobile to avoid conflicts with keyboard
    const timeoutId = setTimeout(() => {
      scrollToBottom();
    }, window.innerWidth <= 768 ? 100 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [messages]);

  // Initialize speech synthesis and set up cleanup
  useEffect(() => {
    if (!checkSpeechSupport()) {
      setSpeechSupported(false);
      return;
    }
    
    setSpeechSupported(true);
    const synth = window.speechSynthesis;
    speechSynth.current = synth;
    
    // Prevent speech from being paused when page loses focus
    const handleVisibilityChange = () => {
      if (document.hidden) {
        synth.pause();
      } else {
        synth.resume();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      synth.cancel();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Firebase auth effect
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (firebaseUser) => {
      if (!firebaseUser) {
        navigate('/login');
        return;
      }
      setUser(firebaseUser);
      const docRef = doc(db, 'users', firebaseUser.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        await setDoc(docRef, {
          profile: { level: "beginner", xp: 0, createdAt: serverTimestamp() },
          progress: { chat: { totalMessages: 0, lastInteraction: serverTimestamp() } },
        });
        setUserProfile({ level: "beginner", xp: 0 });
      } else {
        const data = docSnap.data();
        setUserProfile({
          ...data.profile,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          dateOfBirth: data.dateOfBirth || null
        });
        setXp(data.profile?.xp || 0);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // FIXED: Simplified initialization - no race condition
  useEffect(() => {
    if (userProfile && user && !hasInitialized) {
      initializeConversation();
    }
  }, [userProfile, user, hasInitialized]);

  const logMessageToFirestore = useCallback(async (question, answer, newXp) => {
    if (!user) return;
    
    try {
      const chatRef = collection(db, 'users', user.uid, 'chatSessions');
      await addDoc(chatRef, {
        question,
        answer,
        timestamp: serverTimestamp(),
      });

      const userDoc = doc(db, 'users', user.uid);
      await updateDoc(userDoc, {
        'profile.xp': newXp,
        'progress.chat.totalMessages': (userProfile?.totalMessages || 0) + 1,
        'progress.chat.lastInteraction': serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed to save chat to Firestore:", error);
    }
  }, [user, userProfile]);

  // FIXED: Improved speech handling function
  const handleSpeakMessage = (text, messageId) => {
    if (!speechSynth.current || !speechSupported) {
      console.error("Speech synthesis not available");
      return;
    }
    
    // If clicking the same message that's currently speaking, stop it
    if (speakingMessageId === messageId) {
      speechSynth.current.cancel();
      setSpeakingMessageId(null);
      return;
    }

    // Cancel any current speech and wait a moment for cleanup
    speechSynth.current.cancel();
    
    // Small delay to ensure speech synthesis has fully stopped
    setTimeout(() => {
      // Clean text more thoroughly
      const regex = emojiRegex();
      let cleanedText = text
        .replace(regex, '') // Remove emojis
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove markdown bold
        .replace(/\*(.*?)\*/g, '$1') // Remove markdown italic
        .replace(/`(.*?)`/g, '$1') // Remove inline code
        .replace(/\[(.*?)\]\([^)]*\)/g, '$1') // Remove links
        .replace(/#{1,6}\s/g, '') // Remove markdown headers
        .replace(/\n+/g, ' ') // Replace newlines with spaces
        .replace(/\s+/g, ' ') // Normalize whitespace
        .trim();

      if (!cleanedText) {
        console.warn("No text to speak after cleaning");
        return;
      }

      const setVoiceAndSpeak = () => {
        const utterance = new SpeechSynthesisUtterance(cleanedText);
        utterance.rate = 0.8;
        utterance.pitch = 1.1;
        utterance.volume = 0.8;
        
        // Try to find a suitable voice
        const voices = speechSynth.current.getVoices();
        const englishVoice = voices.find(voice => 
          voice.lang.includes('en') && !voice.name.includes('Google')
        ) || voices.find(voice => voice.lang.includes('en')) || voices[0];
        
        if (englishVoice) {
          utterance.voice = englishVoice;
        }
        
        utterance.onstart = () => {
          setSpeakingMessageId(messageId);
        };
        
        utterance.onend = () => {
          setSpeakingMessageId(null);
        };
        
        utterance.onerror = (event) => {
          console.error("Speech synthesis error:", event.error);
          setSpeakingMessageId(null);
          if (event.error !== 'interrupted' && event.error !== 'canceled') {
            console.warn("Speech synthesis may not be supported. Please enable in browser settings.");
          }
        };
        
        utterance.onpause = () => {
          console.log("Speech paused");
        };
        
        utterance.onresume = () => {
          console.log("Speech resumed");
        };
        
        try {
          speechSynth.current.speak(utterance);
        } catch (error) {
          console.error("Failed to start speech:", error);
          setSpeakingMessageId(null);
        }
      };
      
      // Check if voices are already loaded
      if (speechSynth.current.getVoices().length > 0) {
        setVoiceAndSpeak();
      } else {
        // Wait for voices to load
        const voicesChangedHandler = () => {
          setVoiceAndSpeak();
          speechSynth.current.removeEventListener('voiceschanged', voicesChangedHandler);
        };
        speechSynth.current.addEventListener('voiceschanged', voicesChangedHandler);
        
        // Fallback timeout in case voiceschanged doesn't fire
        setTimeout(() => {
          if (speechSynth.current.getVoices().length === 0) {
            console.warn("No voices loaded, proceeding anyway");
          }
          setVoiceAndSpeak();
        }, 100);
      }
    }, 100); // 100ms delay to ensure proper cleanup
  };

  // FIXED: Single initialization function - no race conditions
  const initializeConversation = useCallback(async () => {
    if (hasInitialized || loading) return;
    
    setLoading(true);
    setHasInitialized(true); // Set this immediately to prevent multiple calls
    
    try {
      const systemMessage = {
        role: "system",
        content: getSystemMessage(userProfile),
      };

      // Call secure Cloud Function for AI response
      const result = await sendChatMessage(
        "Hi, I'm ready to learn!",
        user.uid,
        [systemMessage] // Send system message for context
      );

      const updatedMessages = [systemMessage, { role: "assistant", content: result.response }];
      setMessages(updatedMessages);

      await logMessageToFirestore("Initial greeting", result.response, xp);
    } catch (err) {
      console.error("Error initializing conversation:", err);
      const fallbackGreeting = `Hello! I'm ${AI_NAME} ${AI_EMOJI} your ${AI_ROLE}! مرحبا (pronounced 'marhaba') means 'Hello' in Arabic! Let's learn something fun today! 🎉`;
      
      const updatedMessages = [
        { role: "system", content: getSystemMessage(userProfile) },
        { role: "assistant", content: fallbackGreeting }
      ];
      setMessages(updatedMessages);
    } finally {
      setLoading(false);
    }
  }, [userProfile, user, xp, logMessageToFirestore, hasInitialized, loading]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    // Blur input on mobile to prevent keyboard issues
    if (window.innerWidth <= 768) {
      document.activeElement?.blur();
    }

    const userMessage = { role: "user", content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const result = await sendChatMessage(input, user.uid, messages);
      const assistantMessage = { role: "assistant", content: result.response };
      const finalMessages = [...updatedMessages, assistantMessage];
      setMessages(finalMessages);

      const newXp = xp + 10;
      setXp(newXp);
      await logMessageToFirestore(input, result.response, newXp);
    } catch (err) {
      console.error("Error:", err);
      const errorMessage = { 
        role: "assistant", 
        content: `Sorry! I had a little hiccup. Let's keep learning! 🌟 Can you tell me about your favorite animal in English? I'll teach you the Arabic word for it!` 
      };
      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const StarDisplay = ({ count, size = "text-lg" }) => {
    return (
      <div className="flex justify-center">
        {[...Array(Math.min(count, 3))].map((_, i) => (
          <span key={i} className={`${size} text-yellow-400 animate-sparkle`} style={{animationDelay: `${i * 0.2}s`}}>
            ⭐
          </span>
        ))}
      </div>
    );
  };

  if (!user || !userProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-400 to-yellow-300 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-spin">🐪</div>
          <p className="text-white text-xl font-bold">Loading your learning adventure...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header - Fixed height */}
        <div className="flex-shrink-0 text-center py-4 md:py-8 px-4">
          <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-4 drop-shadow-2xl">
            🤖 Teta Zeitouna AI Tutor 🤖
          </h1>
          <p className="text-lg md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Chat with your personal Arabic AI teacher anytime!
          </p>
        </div>

        {/* Chat Content - Takes remaining space */}
        <div className="flex-1 px-4 pb-4 min-h-0">
          {/* MOBILE LAYOUT */}
          <div className="md:hidden h-full">
            <div className="flex flex-col gap-4 h-full">
              {/* Mobile Sidebar - Compact at top */}
              <div className="flex-shrink-0 bg-white rounded-3xl shadow-2xl p-4 border-4 border-green-300">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <Lottie 
                      animationData={camelAnimation} 
                      loop={true} 
                      style={{ height: 60, width: 60 }} 
                      lottieRef={avatarRef} 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-sm font-bold text-purple-600 mb-1">
                      Hello, {userProfile?.firstName || 'Young Explorer'}! 👋
                    </h2>
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold inline-block">
                      🌟 {xp} points
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex gap-2">
                    <button 
                      onClick={() => navigate('/')}
                      className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-xs hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                    >
                      <FaHome size={12} />
                    </button>
                    <button 
                      onClick={() => {
                        setMessages([]);
                        setHasInitialized(false);
                      }}
                      className="p-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold text-xs hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                      🔄
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Chat Main Area */}
              <div className="flex-1 bg-white rounded-3xl shadow-2xl border-4 border-yellow-300 flex flex-col min-h-0">
                {/* Chat Header */}
                <div className="flex-shrink-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white p-3 rounded-t-3xl">
                  <h2 className="text-lg font-bold text-center">
                    💬 Chat with {AI_NAME}
                  </h2>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-3 bg-gradient-to-b from-blue-50 to-purple-50 min-h-0">
                  {loading && messages.filter(m => m.role !== "system").length === 0 && (
                    <div className="text-center py-8">
                      <div className="bg-white rounded-2xl p-4 shadow-lg border-2 border-blue-200 mx-auto max-w-sm">
                        <div className="text-3xl mb-4 animate-bounce">🐪</div>
                        <p className="text-purple-600 font-bold">
                          {AI_NAME} is getting ready to meet you...
                        </p>
                        <div className="flex justify-center space-x-1 mt-4">
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {messages.filter(m => m.role !== "system").map((msg, i) => {
                    const isSpeaking = speakingMessageId === i;
                    const isUser = msg.role === 'user';
                    
                    return (
                      <div key={i} className={`mb-3 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
                          <div className={`
                            p-3 rounded-2xl shadow-lg border-2 
                            ${isUser 
                              ? 'bg-gradient-to-r from-green-400 to-green-500 border-green-300 text-white' 
                              : 'bg-gradient-to-r from-blue-100 to-purple-100 border-blue-300 text-gray-800'
                            }
                          `}>
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`
                                w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                                ${isUser 
                                  ? 'bg-white text-green-600' 
                                  : 'bg-purple-500 text-white'
                                }
                              `}>
                                {isUser 
                                  ? (userProfile?.firstName?.charAt(0) || 'Y')
                                  : AI_EMOJI
                                }
                              </div>
                              <span className={`font-bold text-xs ${isUser ? 'text-white' : 'text-purple-600'}`}>
                                {isUser ? (userProfile?.firstName || 'You') : AI_NAME}
                              </span>
                              
                              {!isUser && speechSupported && (
                                <button 
                                  onClick={() => handleSpeakMessage(msg.content, i)}
                                  className={`
                                    ml-auto p-1.5 rounded-full transition-all duration-200 flex items-center justify-center
                                    ${isSpeaking 
                                      ? 'bg-yellow-400 shadow-lg animate-pulse' 
                                      : 'bg-white hover:bg-yellow-100'
                                    }
                                  `}
                                  aria-label={isSpeaking ? "Stop speaking" : "Listen to message"}
                                >
                                  <FaVolumeUp size={12} color={isSpeaking ? '#8B5CF6' : '#7C3AED'} />
                                </button>
                              )}
                            </div>
                            
                            <div className="text-sm leading-relaxed">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {msg.content}
                              </ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {loading && (
                    <div className="flex justify-start mb-3">
                      <div className="max-w-[85%]">
                        <div className="bg-gradient-to-r from-blue-100 to-purple-100 border-blue-300 border-2 p-3 rounded-2xl shadow-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              {AI_EMOJI}
                            </div>
                            <span className="font-bold text-xs text-purple-600">{AI_NAME}</span>
                          </div>
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area - Fixed at bottom */}
                <div className="flex-shrink-0 border-t-4 border-yellow-300 bg-white p-3 rounded-b-3xl">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border-2 border-purple-300 rounded-2xl focus:outline-none focus:border-purple-500 text-sm transition-all duration-200"
                      disabled={loading}
                      style={{ fontSize: '16px' }} // Prevent zoom on iOS
                    />
                    <button
                      onClick={sendMessage}
                      disabled={loading || !input.trim()}
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold disabled:opacity-50 hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 text-sm"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <FaPaperPlane size={14} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* DESKTOP LAYOUT - FIXED HEIGHT */}
          <div className="hidden md:block h-full max-w-6xl mx-auto">
            <div className="grid grid-cols-4 gap-4 h-full">
              
              {/* Sidebar */}
              <div className="col-span-1 h-full">
                {/* Avatar Section with Lottie Animation */}
                <div className="bg-white rounded-3xl shadow-2xl p-6 border-4 border-green-300 h-full flex flex-col">
                  <div className="text-center flex-1">
                    <div className="flex justify-center mb-4">
                      <Lottie 
                        animationData={camelAnimation} 
                        loop={true} 
                        style={{ height: 120, width: 120 }} 
                        lottieRef={avatarRef} 
                      />
                    </div>
                    <h2 className="text-xl font-bold text-purple-600 mb-2">
                      Hello, {userProfile?.firstName || 'Young Explorer'}! 👋
                    </h2>
                    <p className="text-base text-gray-600 mb-4">
                      Ready to learn Arabic with {AI_NAME}?
                    </p>
                    
                    <div className="mb-6">
                      <p className="text-sm text-gray-600 mb-2">Your Learning XP</p>
                      <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full font-bold">
                        🌟 {xp} points
                      </div>
                      <StarDisplay count={Math.floor(xp / 30)} />
                    </div>
                  </div>
                  
                  <div className="space-y-3 flex-shrink-0">
                    <button 
                      onClick={() => navigate('/')}
                      className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <FaHome size={16} />
                      Back to Games
                    </button>
                    <button 
                      onClick={() => {
                        setMessages([]);
                        setHasInitialized(false);
                      }}
                      className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                      🔄 New Conversation
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Chat Area - FIXED HEIGHT */}
              <div className="col-span-3 h-full">
                <div className="bg-white rounded-3xl shadow-2xl border-4 border-yellow-300 h-full flex flex-col">
                  {/* Chat Header */}
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-4 rounded-t-3xl flex-shrink-0">
                    <h2 className="text-xl font-bold text-center">
                      💬 Chat with {AI_NAME}
                    </h2>
                  </div>

                  {/* Messages Area - SCROLLABLE WITH FIXED HEIGHT */}
                  <div className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-blue-50 to-purple-50 min-h-0">
                    {loading && messages.filter(m => m.role !== "system").length === 0 && (
                      <div className="text-center py-8">
                        <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200 mx-auto max-w-md">
                          <div className="text-4xl mb-4 animate-bounce">🐪</div>
                          <p className="text-purple-600 font-bold text-lg">
                            {AI_NAME} is getting ready to meet you...
                          </p>
                          <div className="flex justify-center space-x-1 mt-4">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        </div>
                      </div>
                    )}

                    {messages.filter(m => m.role !== "system").map((msg, i) => {
                      const isSpeaking = speakingMessageId === i;
                      const isUser = msg.role === 'user';
                      
                      return (
                        <div key={i} className={`mb-4 flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] ${isUser ? 'order-2' : 'order-1'}`}>
                            <div className={`
                              p-4 rounded-3xl shadow-lg border-4 
                              ${isUser 
                                ? 'bg-gradient-to-r from-green-400 to-green-500 border-green-300 text-white' 
                                : 'bg-gradient-to-r from-blue-100 to-purple-100 border-blue-300 text-gray-800'
                              }
                            `}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`
                                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                                  ${isUser 
                                    ? 'bg-white text-green-600' 
                                    : 'bg-purple-500 text-white'
                                  }
                                `}>
                                  {isUser 
                                    ? (userProfile?.firstName?.charAt(0) || 'Y')
                                    : AI_EMOJI
                                  }
                                </div>
                                <span className={`font-bold text-sm ${isUser ? 'text-white' : 'text-purple-600'}`}>
                                  {isUser ? (userProfile?.firstName || 'You') : AI_NAME}
                                </span>
                                
                                {!isUser && speechSupported && (
                                  <button 
                                    onClick={() => handleSpeakMessage(msg.content, i)}
                                    className={`
                                      ml-auto p-2 rounded-full transition-all duration-200 flex items-center justify-center
                                      ${isSpeaking 
                                        ? 'bg-yellow-400 shadow-lg animate-pulse' 
                                        : 'bg-white hover:bg-yellow-100'
                                      }
                                    `}
                                    aria-label={isSpeaking ? "Stop speaking" : "Listen to message"}
                                    title={isSpeaking ? "Stop speaking" : "Listen to message"}
                                  >
                                    <FaVolumeUp 
                                      size={16} 
                                      color={isSpeaking ? '#8B5CF6' : '#7C3AED'} 
                                      className={isSpeaking ? 'animate-spin' : 'animate-bounce'}
                                      style={{ 
                                        filter: isSpeaking ? 'hue-rotate(120deg)' : 'none'
                                      }}
                                    />
                                  </button>
                                )}
                              </div>
                              
                              <div className="text-base leading-relaxed">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {msg.content}
                                </ReactMarkdown>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {loading && (
                      <div className="flex justify-start mb-4">
                        <div className="bg-gradient-to-r from-blue-100 to-purple-100 p-4 rounded-3xl shadow-lg border-4 border-blue-300 max-w-[80%]">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-bold">
                              {AI_EMOJI}
                            </div>
                            <span className="font-bold text-sm text-purple-600">{AI_NAME}</span>
                          </div>
                          <div className="flex space-x-1 mt-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Area - FIXED AT BOTTOM */}
                  <div className="border-t-4 border-yellow-300 bg-white p-4 rounded-b-3xl flex-shrink-0">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        placeholder="Type your message to start learning Arabic..."
                        className="flex-1 px-4 py-3 border-2 border-purple-300 rounded-2xl focus:outline-none focus:border-purple-500 text-base transition-all duration-200"
                        disabled={loading}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={loading || !input.trim()}
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold disabled:opacity-50 hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <>
                            <FaPaperPlane size={16} />
                            Send
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => alert("🎤 Speech-to-text coming soon!")}
                        className="p-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                      >
                        <FaMicrophone size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Celebration Effects (for XP gains) - Fixed positioning to prevent layout shifts */}
        {xp > 0 && (
          <div className="fixed top-4 right-4 z-50 pointer-events-none hidden md:block">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-2 rounded-full shadow-lg animate-bounce">
              <span className="font-bold">+10 XP! 🌟</span>
            </div>
          </div>
        )}
      </div>

      {/* Mobile CSS fixes */}
      <style jsx>{`
        @media (max-width: 768px) {
          /* Critical mobile viewport fix */
          html, body {
            height: 100%;
            overflow-x: hidden;
            overscroll-behavior: none;
            -webkit-text-size-adjust: 100%;
            -webkit-tap-highlight-color: transparent;
          }
          
          /* Viewport height custom property */
          .min-h-screen {
            min-height: 100vh;
            min-height: calc(var(--vh, 1vh) * 100);
          }
          
          /* Keyboard handling */
          .keyboard-visible .keyboard-adjusted {
            height: calc(var(--vh, 1vh) * 100 - 20rem);
            transition: height 0.3s ease;
          }
          
          /* Input focus handling */
          input:focus {
            position: fixed;
            z-index: 1000;
          }
          
          /* Prevent zoom on input focus */
          input[type="text"] {
            font-size: 16px !important;
          }
          
          /* Better touch targets */
          button {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Scroll improvements */
          .overflow-y-auto {
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }
        }
        
        /* iOS specific fixes */
        @supports (-webkit-touch-callout: none) {
          .min-h-screen {
            min-height: calc(var(--vh, 1vh) * 100);
          }
          
          /* Safe area handling */
          .p-4 {
            padding-left: max(1rem, env(safe-area-inset-left));
            padding-right: max(1rem, env(safe-area-inset-right));
          }
        }
      `}</style>
    </div>
  );
}

export default Chat;